import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../context/AuthContext';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import { 
  Plus, Search, Edit2, Target, Shield, Wallet, ArrowRight
} from 'lucide-react';

const BudgetProfiles = () => {
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editProfile, setEditProfile] = useState(null);
  const [formData, setFormData] = useState({
    profile_id: '',
    name: '',
    description: '',
    budget_for_costs: true,
    budget_for_revenues: false,
    budget_for_payments: false,
    availability_control_active: true,
    check_assigned_budget: true,
    check_distributed_budget: false,
    overall_tolerance_percent: 0,
    annual_tolerance_percent: 0,
    allow_annual_distribution: true,
    allow_period_distribution: true,
    distribution_rule: 'MANUAL',
    require_budget_release: true,
    release_levels: 1,
    allow_carryforward: true,
    carryforward_rule: 'REMAINING',
    update_commitment_on_budget: true,
    link_to_investment_program: false,
    currency_type: 'OBJECT',
    status: 'Active'
  });

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const response = await psAPI.getBudgetProfiles();
      setProfiles(response.data);
    } catch (error) {
      console.error('Error fetching budget profiles:', error);
      toast.error('Failed to load budget profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.profile_id || !formData.name) {
        toast.error('Profile ID and Name are required');
        return;
      }

      if (editProfile) {
        await psAPI.updateBudgetProfile(editProfile.profile_id, formData);
        toast.success('Budget profile updated successfully');
      } else {
        await psAPI.createBudgetProfile(formData);
        toast.success('Budget profile created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      fetchProfiles();
    } catch (error) {
      console.error('Error saving budget profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to save budget profile');
    }
  };

  const openEditDialog = (profile) => {
    setEditProfile(profile);
    setFormData({
      profile_id: profile.profile_id,
      name: profile.name,
      description: profile.description || '',
      budget_for_costs: profile.budget_for_costs,
      budget_for_revenues: profile.budget_for_revenues,
      budget_for_payments: profile.budget_for_payments,
      availability_control_active: profile.availability_control_active,
      check_assigned_budget: profile.check_assigned_budget,
      check_distributed_budget: profile.check_distributed_budget,
      overall_tolerance_percent: profile.overall_tolerance_percent,
      annual_tolerance_percent: profile.annual_tolerance_percent,
      allow_annual_distribution: profile.allow_annual_distribution,
      allow_period_distribution: profile.allow_period_distribution,
      distribution_rule: profile.distribution_rule,
      require_budget_release: profile.require_budget_release,
      release_levels: profile.release_levels,
      allow_carryforward: profile.allow_carryforward,
      carryforward_rule: profile.carryforward_rule,
      update_commitment_on_budget: profile.update_commitment_on_budget,
      link_to_investment_program: profile.link_to_investment_program,
      currency_type: profile.currency_type,
      status: profile.status
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditProfile(null);
    setFormData({
      profile_id: '',
      name: '',
      description: '',
      budget_for_costs: true,
      budget_for_revenues: false,
      budget_for_payments: false,
      availability_control_active: true,
      check_assigned_budget: true,
      check_distributed_budget: false,
      overall_tolerance_percent: 0,
      annual_tolerance_percent: 0,
      allow_annual_distribution: true,
      allow_period_distribution: true,
      distribution_rule: 'MANUAL',
      require_budget_release: true,
      release_levels: 1,
      allow_carryforward: true,
      carryforward_rule: 'REMAINING',
      update_commitment_on_budget: true,
      link_to_investment_program: false,
      currency_type: 'OBJECT',
      status: 'Active'
    });
  };

  const filteredProfiles = profiles.filter(p =>
    p.profile_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="budget-profiles-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Budget Profiles</h1>
          <p className="text-gray-500">Configure budget control and availability settings</p>
        </div>
        {canWrite('budget_management') && (
        <Button onClick={() => setShowDialog(true)}>
          <Plus className="h-4 w-4 mr-2" /> New Profile
        </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              className="pl-9"
              placeholder="Search profiles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Profiles List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Budget Profiles
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            </div>
          ) : filteredProfiles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No budget profiles found. Create your first budget profile.
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredProfiles.map((profile) => (
                <div 
                  key={profile.profile_id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{profile.profile_id}</h3>
                        <Badge className={profile.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {profile.status}
                        </Badge>
                      </div>
                      <p className="text-gray-600 mt-1">{profile.name}</p>
                      {profile.description && (
                        <p className="text-sm text-gray-500 mt-1">{profile.description}</p>
                      )}
                      
                      <div className="flex flex-wrap gap-4 mt-4">
                        {/* Budget Categories */}
                        <div className="flex items-center gap-2 text-sm">
                          <Wallet className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600">Categories:</span>
                          {profile.budget_for_costs && <Badge variant="outline" className="text-xs">Costs</Badge>}
                          {profile.budget_for_revenues && <Badge variant="outline" className="text-xs">Revenues</Badge>}
                          {profile.budget_for_payments && <Badge variant="outline" className="text-xs">Payments</Badge>}
                        </div>
                        
                        {/* Availability Control */}
                        <div className="flex items-center gap-2 text-sm">
                          <Shield className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600">Availability:</span>
                          <Badge variant={profile.availability_control_active ? 'default' : 'outline'} className="text-xs">
                            {profile.availability_control_active ? 'Active' : 'Inactive'}
                          </Badge>
                          {profile.overall_tolerance_percent > 0 && (
                            <span className="text-xs text-gray-500">({profile.overall_tolerance_percent}% tolerance)</span>
                          )}
                        </div>
                        
                        {/* Release */}
                        {profile.require_budget_release && (
                          <div className="flex items-center gap-2 text-sm">
                            <ArrowRight className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">Release:</span>
                            <span className="text-xs">{profile.release_levels} level(s)</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(profile)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editProfile ? 'Edit Budget Profile' : 'Create Budget Profile'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Profile ID *</Label>
                <Input 
                  placeholder="e.g., BGT_STD"
                  value={formData.profile_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, profile_id: e.target.value.toUpperCase() }))}
                  disabled={!!editProfile}
                />
              </div>
              <div>
                <Label>Name *</Label>
                <Input 
                  placeholder="Profile name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea 
                  placeholder="Description..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>

            {/* Budget Categories */}
            <div>
              <h3 className="font-semibold mb-3">Budget Categories</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.budget_for_costs}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, budget_for_costs: v }))}
                  />
                  <Label>Budget for Costs</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.budget_for_revenues}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, budget_for_revenues: v }))}
                  />
                  <Label>Budget for Revenues</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.budget_for_payments}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, budget_for_payments: v }))}
                  />
                  <Label>Budget for Payments</Label>
                </div>
              </div>
            </div>

            {/* Availability Control */}
            <div>
              <h3 className="font-semibold mb-3">Availability Control</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.availability_control_active}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, availability_control_active: v }))}
                  />
                  <Label>Enable Availability Control</Label>
                </div>
                {formData.availability_control_active && (
                  <div className="grid grid-cols-2 gap-4 ml-6">
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={formData.check_assigned_budget}
                        onCheckedChange={(v) => setFormData(prev => ({ ...prev, check_assigned_budget: v }))}
                      />
                      <Label className="text-sm">Check Assigned Budget</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={formData.check_distributed_budget}
                        onCheckedChange={(v) => setFormData(prev => ({ ...prev, check_distributed_budget: v }))}
                      />
                      <Label className="text-sm">Check Distributed Budget</Label>
                    </div>
                    <div>
                      <Label className="text-sm">Overall Tolerance %</Label>
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={formData.overall_tolerance_percent}
                        onChange={(e) => setFormData(prev => ({ ...prev, overall_tolerance_percent: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Annual Tolerance %</Label>
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={formData.annual_tolerance_percent}
                        onChange={(e) => setFormData(prev => ({ ...prev, annual_tolerance_percent: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Distribution */}
            <div>
              <h3 className="font-semibold mb-3">Budget Distribution</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.allow_annual_distribution}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, allow_annual_distribution: v }))}
                  />
                  <Label>Allow Annual Distribution</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.allow_period_distribution}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, allow_period_distribution: v }))}
                  />
                  <Label>Allow Period Distribution</Label>
                </div>
                <div>
                  <Label>Distribution Rule</Label>
                  <Select 
                    value={formData.distribution_rule} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, distribution_rule: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EQUAL">Equal Distribution</SelectItem>
                      <SelectItem value="WEIGHTED">Weighted Distribution</SelectItem>
                      <SelectItem value="MANUAL">Manual Distribution</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Release Control */}
            <div>
              <h3 className="font-semibold mb-3">Release Control</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.require_budget_release}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, require_budget_release: v }))}
                  />
                  <Label>Require Budget Release</Label>
                </div>
                {formData.require_budget_release && (
                  <div>
                    <Label>Release Levels</Label>
                    <Select 
                      value={formData.release_levels.toString()} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, release_levels: parseInt(v) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Level</SelectItem>
                        <SelectItem value="2">2 Levels</SelectItem>
                        <SelectItem value="3">3 Levels</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Carryforward */}
            <div>
              <h3 className="font-semibold mb-3">Budget Carryforward</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.allow_carryforward}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, allow_carryforward: v }))}
                  />
                  <Label>Allow Carryforward</Label>
                </div>
                {formData.allow_carryforward && (
                  <div>
                    <Label>Carryforward Rule</Label>
                    <Select 
                      value={formData.carryforward_rule} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, carryforward_rule: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FULL">Full Budget</SelectItem>
                        <SelectItem value="REMAINING">Remaining Budget Only</SelectItem>
                        <SelectItem value="NONE">No Carryforward</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div>
              <Label>Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>{editProfile ? 'Update' : 'Create'} Profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default BudgetProfiles;
